import type { TransformNode, TransformEdge } from './transform';

// ============================================================================
// New step-list model (v2)
// ============================================================================

export type PipelineTrigger = {
  type: 'none' | 'datasource';
  dataSourceId?: string;
  dataSourceName?: string;
  connectorType?: string;
  /** Pipeline-level streams: stream_name → cursor_field | null */
  streams?: Record<string, string | null>;
};

export type TransformStep = {
  id: string;
  name: string;
  transformNodes: TransformNode[];
  transformEdges: TransformEdge[];
  dsl: string;
  outputNamespace: string;
  outputTable: string;
  mode: 'replace' | 'merge' | 'append' | 'upsert';
  conflictKeys: string[];
  partitionBy?: string[];
  dependsOn: string[];
  /** Steps that must FAIL for this step to run (recovery flow) */
  dependsOnFailure?: string[];
  order: number;
};

/** Combine namespace + table into the qualified output name */
export function fullOutputTable(step: TransformStep): string {
  if (step.outputNamespace && step.outputTable) {
    return `${step.outputNamespace}.${step.outputTable}`;
  }
  return step.outputTable;
}

export type IngestStep = {
  id: string;
  name: string;
  /** Full connector DSL (e.g. connector("github-graphql", ...).stream("pull_requests").output(...)) */
  dsl: string;
  /** Connector type extracted from DSL */
  connectorType: string;
  /** Stream name extracted from DSL */
  streamName: string;
  /** Linked DataSource ID — each ingest step picks its own DataSource */
  dataSourceId?: string;
  /** Display name for the linked DataSource */
  dataSourceName?: string;
  order: number;
  /** Names of other steps this ingest step depends on */
  dependsOn: string[];
  /** Steps that must FAIL for this step to run (recovery flow) */
  dependsOnFailure?: string[];
};

export type PipelineChart = {
  id: string;
  name: string;
  subtitle?: string;
  description?: string;
  dependsOn: string[];
  dsl: string;
  drilldown_dsl?: string;
  tab?: string;
  grid?: { x: number; y: number; w: number; h: number };
};

type AlertSeverity = 'info' | 'warning' | 'critical';

/** Lifecycle trigger — when set, the alert fires after pipeline completion, not during DAG execution */
export type AlertTrigger = 'on_success' | 'on_failure' | 'on_complete' | null;

export type AlertStep = {
  id: string;
  name: string;
  title: string;
  description: string;
  severity: AlertSeverity;
  dsl: string;
  channels: string[];
  dependsOn: string[];
  /** Steps that must FAIL for this step to run (recovery flow) */
  dependsOnFailure?: string[];
  order: number;
  /** null = normal DAG alert, 'on_success'/'on_failure'/'on_complete' = lifecycle hook */
  trigger: AlertTrigger;
};

// ============================================================================
// Backward-compat canvas types (v1 — used for migration)
// ============================================================================

type StreamConfig = {
  name: string;
  cursorField: string | null;
  selected: boolean;
  schema?: Record<string, unknown>;
  primaryKey?: string[];
  requires?: string[];
  schemaName?: string;
  syncMode?: 'full_refresh' | 'incremental';
  availableCursorFields?: string[];
};

type StreamRequirement = {
  name: string;
  description?: string;
  requires: string[];
};

export type SourceNodeData = {
  connectorName?: string;
  connectorTitle?: string;
  connectorIcon?: string;
  connectorCategory?: string;
  connectorSpec?: Record<string, unknown>;
  connectorAuthType?: string;
  credentials?: Record<string, unknown>;
  namespace?: string;
  streams?: string[];
  streamConfigs?: Record<string, StreamConfig>;
  streamRequirements?: StreamRequirement[];
  statePath?: string;
  maxRecords?: number;
  connectionTested?: boolean;
  connectionError?: string | null;
  discoveredStreams?: StreamConfig[];
  hasPredefinedStreams?: boolean;
};

type OutputNodeData = {
  format?: 'solidafy';
  outputConfig?: Record<string, unknown>;
};

export type MaterializeNodeData = {
  name?: string;
  transformNodes?: TransformNode[];
  transformEdges?: TransformEdge[];
  dsl?: string;
  outputTable?: string;
  mode?: 'replace' | 'merge' | 'append' | 'upsert';
  conflictKeys?: string[];
  partitionBy?: string[];
  dependsOn?: string[];
  layer?: 'bronze' | 'silver' | 'gold';
};

export type PipelineNode = {
  id: string;
  type: 'source' | 'output' | 'transform' | 'materialize';
  position: { x: number; y: number };
  data: SourceNodeData | OutputNodeData | MaterializeNodeData | Record<string, unknown>;
  configured: boolean;
};

export type PipelineTemplate = {
  name: string;
  title: string;
  description?: string;
  connector: string;
  connectors?: string[];
  streams: string[];
  outputFormat: string;
  materializations?: string[];
  yaml?: string;
};
